"use client";

import type { AgentTemplate, AgentTemplateDocLinks, AgentTemplateIntegrationMap, AgentTemplateVoiceMap } from "./types";

import { useLocalePrefix } from "@/app/_utilities/i18n";
import { useDeepLinkModal } from "@ui/hooks/useDeepLinkModal";
import { createContext, useCallback, useContext } from "react";
import { getAgentTemplatePath } from "./utils";
import dynamic from "next/dynamic";

const LazyAgentTemplatePopupDialog = dynamic(() =>
  import("./AgentTemplatePopupDialog").then(m => m.AgentTemplatePopupDialog),
);

interface TemplatePopupData {
  docLinks: AgentTemplateDocLinks;
  integrationMap: AgentTemplateIntegrationMap;
  templates: AgentTemplate[];
  voiceMap: AgentTemplateVoiceMap;
}

interface TemplatePopupState {
  openTemplate: (templateId: string) => void;
}

const AgentTemplatePopupContext = createContext<TemplatePopupState | null>(null);

export function useAgentTemplatePopup() {
  const ctx = useContext(AgentTemplatePopupContext);
  if (!ctx) {
    throw new Error("useAgentTemplatePopup must be used within AgentTemplatePopupProvider");
  }
  return ctx;
}

export function AgentTemplatePopupProvider({ children, data }: { children: React.ReactNode; data: TemplatePopupData }) {
  const localePrefix = useLocalePrefix();

  // Deep links here are real SSR routes, so the popup is only ever opened by a
  // click — no `getIdFromUrl`, so Back just closes (the route's own inline popup
  // handles direct navigation to a template URL).
  const getUrlForId = useCallback(
    (templateId: string) => {
      const template = data.templates.find(t => t.template_id === templateId);
      return `${localePrefix}${getAgentTemplatePath(templateId, template?.name)}`;
    },
    [data.templates, localePrefix],
  );

  const {
    selectedId: selectedTemplateId,
    isOpen,
    open: openTemplate,
    close: closePopup,
  } = useDeepLinkModal({ getUrlForId });

  const selectedTemplate = selectedTemplateId
    ? (data.templates.find(t => t.template_id === selectedTemplateId) ?? null)
    : null;

  return (
    <AgentTemplatePopupContext.Provider value={{ openTemplate }}>
      {children}
      {selectedTemplate && (
        <LazyAgentTemplatePopupDialog
          docLinks={data.docLinks}
          integrationMap={data.integrationMap}
          isOpen={isOpen}
          onClose={closePopup}
          template={selectedTemplate}
          voiceMap={data.voiceMap}
        />
      )}
    </AgentTemplatePopupContext.Provider>
  );
}
